import { Fragment } from 'react';
import { Badge, Button, Card, Col, Form, Overlay, Popover, Row, Spinner, Table } from 'react-bootstrap';

import { TipoPeriodoDescanso } from '@/modules/business/periodo/enums/tipo-periodo-descanso.enum';
import { diasDaSemana } from '@/shared/utils/datas';

import type { RelatorioCivelStepProps } from './relatorio-civel-step-props';

export default function Step01Comparecimento(props: RelatorioCivelStepProps) {
  const {
    formData,
    isViewMode,
    origemVisualizacao,
    cardStyle,
    headerStyle,
    RELATORIO_CORES,
    justificativaButtonRefs,
    justificativaModalDia,
    justificativaTextoTemp,
    setJustificativaTextoTemp,
    justificativaArquivoTemp,
    fileInputRef,
    handleFileChange,
    salvarJustificativa,
    cancelarJustificativa,
    gerarCalendarioMensal,
    contarImpedimentosNoPeriodo,
    podeCorregedoriaAgirComparecimento,
    diasComparecimentoCorregedoriaPendentes,
    corregedoriaDecisaoDiaLoading,
    aplicarDecisaoCorregedoriaNoDia,
    getMesLabel,
    getNomeSemana,
    contarDiasUteisSemana,
    getPresencaMinimaObrigatoria,
    semanaJaPassou,
    getStatusDia,
    isDiaUtil,
    isDiaFuturo,
    getImpedimentoDia,
    getFeriadosNaData,
    abrirJustificativaModal,
    atualizarStatusDia,
    getTipoPeriodoNome,
  } = props;

  const mesNorm = String(formData.mes).padStart(2, '0');
  const calendario = gerarCalendarioMensal(mesNorm, formData.ano);
  const diasStatus = formData.comparecimento.diasStatus || [];
  // Presença: apenas 'presente'; 'remoto' só conta quando corregedoria validou (remotoValidado === true)
  const diasPresentes = diasStatus.filter(
    (d) => d.status === 'presente' || (d.status === 'remoto' && d.remotoValidado === true),
  ).length;
  // `contarImpedimentosNoPeriodo` é usado em outros trechos da etapa; aqui não precisamos armazenar em variável.
  contarImpedimentosNoPeriodo();
  const diasAverbacaoPendentes = diasStatus.some(
    (d) =>
      (d.status === 'ausente' && d.ausenteAceitoCorregedoria !== true) ||
      (d.status === 'remoto' && d.remotoValidado !== true),
  );

  return (
    <Fragment>
      {/* Legenda com quadradinhos coloridos */}
      <Card className="mb-3" style={cardStyle}>
        <Card.Body className="py-2">
          <div className="d-flex flex-wrap gap-4 align-items-center justify-content-center">
            <span className="d-flex align-items-center gap-1">
              <span style={{ width: 14, height: 14, backgroundColor: '#198754', borderRadius: 2 }} aria-hidden />
              <small>Presente</small>
            </span>
            <span className="d-flex align-items-center gap-1">
              <span style={{ width: 14, height: 14, backgroundColor: '#ffc107', borderRadius: 2 }} aria-hidden />
              <small>Ausente</small>
            </span>
            <span className="d-flex align-items-center gap-1">
              <span style={{ width: 14, height: 14, backgroundColor: '#0d6efd', borderRadius: 2 }} aria-hidden />
              <small>Remoto</small>
            </span>
            <span className="d-flex align-items-center gap-1">
              <span style={{ width: 14, height: 14, backgroundColor: '#6c757d', borderRadius: 2 }} aria-hidden />
              <small>Impedimento</small>
            </span>
          </div>
        </Card.Body>
      </Card>

      {isViewMode && origemVisualizacao === 'corregedoria' && diasAverbacaoPendentes && (
        <div className="alert alert-warning" role="alert" style={{ borderLeft: '5px solid #ffc107' }}>
          <i className="bi bi-exclamation-triangle text-warning blink-tri me-2" aria-hidden />
          <strong>Atenção:</strong> existem dias marcados como <strong>Remoto</strong> ou <strong>Ausente</strong> sem
          decisão da Corregedoria. Use a tabela abaixo ou o fluxo na lista de relatórios antes do aval final.
        </div>
      )}

      {podeCorregedoriaAgirComparecimento && (
        <Card className="mb-3" style={cardStyle}>
          <Card.Header style={headerStyle}>
            <h6 className="mb-0 text-center">Corregedoria — decisão por dia (remoto / ausência)</h6>
          </Card.Header>
          <Card.Body>
            {diasComparecimentoCorregedoriaPendentes.length === 0 ? (
              <p className="mb-0 text-muted small">
                Não há dias remoto ou ausente pendentes. Você pode voltar à lista da Corregedoria e registrar o aval
                final.
              </p>
            ) : (
              <div className="table-responsive">
                <Table bordered size="sm" className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Data</th>
                      <th>Tipo</th>
                      <th>Justificativa</th>
                      <th style={{ minWidth: 200 }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diasComparecimentoCorregedoriaPendentes.map((d) => {
                      const mesR = String(d.mes ?? formData.mes).padStart(2, '0');
                      const anoR = d.ano ?? formData.ano;
                      const rowKey = `${d.dia}-${mesR}-${anoR}`;
                      const busy = corregedoriaDecisaoDiaLoading === rowKey;
                      const labelData = `${d.dia} ${getMesLabel(mesR)}/${anoR}`;
                      return (
                        <tr key={`${d.status}-${rowKey}`}>
                          <td>{labelData}</td>
                          <td>{d.status === 'remoto' ? 'Remoto' : 'Ausente'}</td>
                          <td>
                            <small className="text-muted">{d.justificativaTexto?.trim() || '—'}</small>
                          </td>
                          <td>
                            <div className="d-flex flex-wrap gap-1">
                              {d.status === 'remoto' ? (
                                <>
                                  <Button
                                    size="sm"
                                    variant="success"
                                    disabled={busy}
                                    onClick={() => aplicarDecisaoCorregedoriaNoDia(d, 'remoto_validar')}
                                  >
                                    {busy ? <Spinner animation="border" size="sm" /> : 'Validar presença'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    disabled={busy}
                                    onClick={() => aplicarDecisaoCorregedoriaNoDia(d, 'remoto_rejeitar')}
                                  >
                                    Anular dia
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    variant="success"
                                    disabled={busy}
                                    onClick={() => aplicarDecisaoCorregedoriaNoDia(d, 'ausencia_aceitar')}
                                  >
                                    {busy ? <Spinner animation="border" size="sm" /> : 'Aceitar ausência'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    disabled={busy}
                                    onClick={() => aplicarDecisaoCorregedoriaNoDia(d, 'ausencia_rejeitar')}
                                  >
                                    Anular dia
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Calendário Mensal */}
      <Card className="mb-4" style={cardStyle}>
        <Card.Header style={headerStyle}>
          <div className="d-flex align-items-center">
            <h6 className="mb-0 flex-grow-1 text-center">
              {getMesLabel(formData.mes)}/{formData.ano}
            </h6>
            <Badge bg="light" text="dark">
              {diasPresentes} dia{diasPresentes !== 1 ? 's' : ''} de presença
            </Badge>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          <Table bordered className="mb-0" style={{ tableLayout: 'fixed' }}>
            <thead>
              <tr className="table-light">
                <th style={{ width: '120px', textAlign: 'center', fontWeight: 'bold' }}>Semana</th>
                {diasDaSemana.map((dia) => (
                  <th
                    key={dia}
                    style={{
                      textAlign: 'center',
                      fontWeight: 'bold',
                    }}
                  >
                    {dia}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {calendario.map((semana, semanaIndex: number) => {
                const diasUteisSemana = contarDiasUteisSemana(semana);
                const obrigatorios = getPresencaMinimaObrigatoria(diasUteisSemana);
                // Contar presenças apenas nos dias do mês do relatório (mesma base dos dias úteis)
                const presencasNaSemana = semana
                  .filter((dia) => {
                    if (dia === null || !dia.isMesAtual) return false;
                    const status = getStatusDia(dia.dia, dia.mes, dia.ano);
                    return status?.status === 'presente';
                  })
                  .length;
                const concluido = obrigatorios > 0 && presencasNaSemana >= obrigatorios;
                const semanaEncerrada = semanaJaPassou(semana);
                const emFalta = semanaEncerrada && obrigatorios > 0 && !concluido;

                return (
                  <tr key={semanaIndex}>
                    <td
                      style={{
                        textAlign: 'center',
                        fontWeight: 'bold',
                        verticalAlign: 'middle',
                        fontSize: '0.85rem',
                        ...(emFalta ? { backgroundColor: '#dc3545', color: '#fff' } : {}),
                      }}
                    >
                      <div>{getNomeSemana(semanaIndex)}</div>
                      {obrigatorios > 0 && (
                        <small className="d-block mt-1" style={{ fontSize: '0.75rem', fontWeight: 'normal' }}>
                          {presencasNaSemana}/{obrigatorios} dia(s) obrigatório(s)
                        </small>
                      )}
                    </td>
                    {semana.map((dia, diaIndex: number) => {
                      const isDiaUtilAtual = dia !== null && isDiaUtil(dia.dia, dia.mes, dia.ano);
                      const isFuturo = dia !== null && isDiaFuturo(dia.dia, dia.mes, dia.ano);
                      const statusDia = dia !== null ? getStatusDia(dia.dia, dia.mes, dia.ano) : undefined;
                      const isMesAtual = dia !== null ? dia.isMesAtual : false;
                      const impedimentoDia = dia !== null ? getImpedimentoDia(dia.dia, dia.mes, dia.ano) : null;
                      const temImpedimento = impedimentoDia !== null;

                      // Verificar se o dia é feriado/bloqueio (para destacar em amarelo)
                      const feriadosNaData =
                        dia !== null ? getFeriadosNaData(new Date(dia.ano, parseInt(dia.mes) - 1, dia.dia)) : [];
                      const temFeriado = feriadosNaData.length > 0;
                      const temFerias = impedimentoDia?.tipo === TipoPeriodoDescanso.Ferias;
                      const temDiaSemObrigacao = temFeriado || temFerias;

                      const valorSelectAtual = temImpedimento ? 'impedimento' : statusDia?.status || 'nao_marcado';

                      return (
                        <td
                          key={diaIndex}
                          ref={(el) => {
                            if (dia !== null) justificativaButtonRefs.current[dia.dia] = el;
                          }}
                          style={{
                            textAlign: 'center',
                            padding: '8px 4px',
                            verticalAlign: 'middle',
                            opacity: !isMesAtual ? 0.5 : 1,
                            backgroundColor:
                              emFalta && dia !== null && diaIndex !== 0 && diaIndex !== 6
                                ? '#dc3545'
                                : temDiaSemObrigacao
                                  ? '#fff3cd'
                                  : undefined,
                            color:
                              emFalta && dia !== null && diaIndex !== 0 && diaIndex !== 6 ? '#fff' : undefined,
                          }}
                          title={
                            temDiaSemObrigacao
                              ? [
                                  ...(temFeriado ? feriadosNaData.map((f) => f.nome) : []),
                                  ...(temFerias ? ['Férias'] : []),
                                ].join(', ')
                              : undefined
                          }
                        >
                          {dia !== null && (
                            <div className="d-flex flex-column align-items-center">
                              <span style={{ fontSize: '1rem', fontWeight: 'bold' }}>{dia.dia}</span>

                              {/* Select para dias úteis não futuros - apenas do mês atual */}
                              {/* Mostrar select apenas para dias do período do relatório atual */}
                              {isDiaUtilAtual && !isFuturo && isMesAtual && (
                                <Form.Select
                                  size="sm"
                                  value={valorSelectAtual}
                                  onChange={(e) => {
                                    const valor = e.target.value as
                                      | 'presente'
                                      | 'ausente'
                                      | 'remoto'
                                      | 'impedimento'
                                      | 'nao_marcado';
                                    if (valor === 'ausente') {
                                      abrirJustificativaModal(dia.dia, dia.mes, dia.ano);
                                    } else if (valor === 'impedimento') {
                                      // Se tem impedimento cadastrado, usar ele
                                      if (impedimentoDia) {
                                        atualizarStatusDia(
                                          dia.dia,
                                          'impedimento',
                                          undefined,
                                          null,
                                          impedimentoDia.tipo,
                                          getTipoPeriodoNome(impedimentoDia.tipo),
                                          dia.mes,
                                          dia.ano,
                                        );
                                      }
                                    } else {
                                      atualizarStatusDia(
                                        dia.dia,
                                        valor,
                                        undefined,
                                        undefined,
                                        undefined,
                                        undefined,
                                        dia.mes,
                                        dia.ano,
                                      );
                                    }
                                  }}
                                  disabled={temImpedimento}
                                  style={{
                                    fontSize: '0.65rem',
                                    padding: '2px 4px',
                                    marginTop: '4px',
                                    width: '95%',
                                    borderRadius: '4px',
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                    backgroundColor:
                                      valorSelectAtual === 'presente'
                                        ? '#198754'
                                        : valorSelectAtual === 'ausente'
                                          ? '#ffc107'
                                          : valorSelectAtual === 'remoto'
                                            ? '#0d6efd'
                                            : undefined,
                                    color:
                                      valorSelectAtual === 'presente' ||
                                      valorSelectAtual === 'ausente' ||
                                      valorSelectAtual === 'remoto'
                                        ? '#fff'
                                        : undefined,
                                  }}
                                >
                                  <option value="nao_marcado">--</option>
                                  <option value="presente">Presente</option>
                                  <option value="ausente">Ausente</option>
                                  <option value="remoto">Remoto</option>
                                  {temImpedimento && (
                                    <option value="impedimento">{getTipoPeriodoNome(impedimentoDia.tipo)}</option>
                                  )}
                                </Form.Select>
                              )}
                              {/* Indicação visual de feriado/bloqueio */}
                              {temDiaSemObrigacao && (
                                <small
                                  style={{
                                    fontSize: '0.65rem',
                                    marginTop: '4px',
                                    color: '#856404',
                                    fontWeight: 600,
                                  }}
                                >
                                  {temFerias ? 'Férias' : 'Feriado'}
                                </small>
                              )}
                              {/* Mostrar apenas visualização para dias de outros períodos */}
                              {isDiaUtilAtual && !isFuturo && !isMesAtual && (
                                <div
                                  className="text-muted"
                                  style={{
                                    fontSize: '0.65rem',
                                    marginTop: '4px',
                                    fontStyle: 'italic',
                                    cursor: 'not-allowed',
                                  }}
                                  title={`Este dia pertence ao período de ${getMesLabel(dia.mes)}/${dia.ano} e não pode ser editado no relatório de ${getMesLabel(formData.mes)}/${formData.ano}`}
                                >
                                  {statusDia?.status === 'presente' && '✓ Presente'}
                                  {statusDia?.status === 'ausente' && '✗ Ausente'}
                                  {statusDia?.status === 'remoto' && '◐ Remoto'}
                                  {statusDia?.status === 'impedimento' && '⊘ Impedimento'}
                                  {!statusDia && '--'}
                                </div>
                              )}

                              {/* Indicador de impedimento */}
                              {temImpedimento && isDiaUtilAtual && !isFuturo && (
                                <small
                                  style={{ fontSize: '0.55rem', marginTop: '2px' }}
                                  title={impedimentoDia.motivo || ''}
                                >
                                  <i className="bi bi-calendar-x me-1"></i>
                                  {getTipoPeriodoNome(impedimentoDia.tipo).substring(0, 8)}...
                                </small>
                              )}

                              {/* Ícone de justificativa - apenas para dias do período atual */}
                              {statusDia?.status === 'ausente' &&
                                (statusDia.justificativaTexto || statusDia.justificativaArquivo) &&
                                isMesAtual && (
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 mt-1"
                                    onClick={() => abrirJustificativaModal(dia.dia, dia.mes, dia.ano)}
                                    title="Ver/Editar justificativa"
                                  >
                                    <i className="bi bi-file-text"></i>
                                  </Button>
                                )}

                              {/* Indicador de dia futuro */}
                              {isFuturo && (
                                <small style={{ fontSize: '0.6rem' }}>
                                  <i className="bi bi-lock"></i>
                                </small>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
        <Card.Footer className="bg-light">
          <Row>
            <Col md={4} className="text-end">
              <Badge className="me-2" style={{ backgroundColor: RELATORIO_CORES.PRIMARIA }}>
                <i className="bi bi-check-circle me-1"></i>
                {diasPresentes} Presença{diasPresentes !== 1 ? 's' : ''}
              </Badge>
            </Col>
          </Row>
        </Card.Footer>
      </Card>

      {/* Modal de Justificativa */}
      <Overlay
        show={justificativaModalDia !== null}
        target={justificativaModalDia !== null ? justificativaButtonRefs.current[justificativaModalDia] : null}
        placement="auto"
        containerPadding={20}
        rootClose
        rootCloseEvent="click"
        onHide={cancelarJustificativa}
      >
        <Popover id="popover-justificativa" style={{ maxWidth: '400px' }}>
          <Popover.Header as="h3" className="bg-warning text-dark">
            <i className="bi bi-file-earmark-text me-2"></i>
            Justificativa de Ausência - Dia {justificativaModalDia}
          </Popover.Header>
          <Popover.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Texto da Justificativa</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Descreva o motivo da ausência..."
                value={justificativaTextoTemp}
                onChange={(e) => setJustificativaTextoTemp(e.target.value)}
                style={{ borderRadius: '8px' }}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Arquivo de Comprovação</Form.Label>
              <Form.Control type="file" ref={fileInputRef} onChange={handleFileChange} style={{ borderRadius: '8px' }} />
              {justificativaArquivoTemp && (
                <small className="text-muted d-block mt-1">
                  <i className="bi bi-paperclip me-1"></i>
                  {justificativaArquivoTemp.name}
                </small>
              )}
            </Form.Group>
            <div className="d-flex gap-2 justify-content-end">
              <Button variant="secondary" size="sm" onClick={cancelarJustificativa} style={{ borderRadius: '8px' }}>
                <i className="bi bi-x-lg me-1"></i>Cancelar
              </Button>
              <Button variant="warning" size="sm" onClick={salvarJustificativa} style={{ borderRadius: '8px' }}>
                <i className="bi bi-check-lg me-1"></i>Salvar Ausência
              </Button>
            </div>
          </Popover.Body>
        </Popover>
      </Overlay>
    </Fragment>
  );
}

